import { faGripLines, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Label, Select } from '@kcftech/design-system';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { TextInput } from 'components/forms/TextInput';
import {
    stringDefaultValueRules,
    validValueMaxLength,
} from 'pages/Settings/AssemblyType/parameter_form/shared';
import {
    AssemblyParameterFormInputProps,
    TypedAssemblyParameterFormData,
    typeGuardAssemblyParameterForm,
} from 'pages/Settings/AssemblyType/parameter_form/types';
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Controller, ControllerProps, RegisterOptions, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { backgroundColor, useThemeValues } from 'styles/theme';

const ValidValueInputContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2rem;
`;

const ValidValuesListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin: 2rem 2rem 1rem 2rem;
`;

const getDefaultValueRules = (
    validValues: string[] | undefined
): RegisterOptions<TypedAssemblyParameterFormData<'selection'>, 'defaultValue'> => ({
    ...stringDefaultValueRules,
    validate: (value) => {
        if (!value) {
            return undefined;
        }
        if (!validValues?.includes(value)) {
            return 'Default value must be in selections';
        }
        return undefined;
    },
});

export function AssemblyParameterSelectionFormContent({
    form: _form,
}: AssemblyParameterFormInputProps) {
    const form = typeGuardAssemblyParameterForm(_form, 'selection');

    const {
        register,
        formState: { errors },
        control,
    } = form;

    const validValues = useWatch({ control, name: 'validValues' });
    const options = useMemo(
        () => [
            { title: 'None', value: '' },
            ...(validValues ?? []).map((value) => ({ title: value, value })),
        ],
        [validValues]
    );

    return (
        <>
            <Controller
                control={form.control}
                name="validValues"
                render={(props) => <ValidValuesInput {...props} />}
            />

            <Select
                label="Default Selection"
                error={errors.defaultValue?.message}
                options={options}
                {...register('defaultValue', getDefaultValueRules(validValues))}
            />
        </>
    );
}

type ValidValuesProps = Parameters<
    ControllerProps<TypedAssemblyParameterFormData<'selection'>, 'validValues'>['render']
>[0];

function ValidValuesInput({ field: { onChange, value: validValues } }: ValidValuesProps) {
    const [validValue, setValidValue] = useState('');
    const validValueTrimmed = useMemo(() => validValue.trim(), [validValue]);

    const inputIsValid = useMemo(
        () =>
            validValueTrimmed.length > 0 &&
            (!validValues || !validValues.includes(validValueTrimmed)),
        [validValueTrimmed, validValues]
    );

    const onAddValidValue = () => {
        if (inputIsValid) {
            const list = validValues ?? [];
            onChange([...list, validValueTrimmed]);
            setValidValue('');
        }
    };

    const onTextInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.code === 'Enter') {
            onAddValidValue();
            event.preventDefault();
            event.stopPropagation();
        }
    };

    return (
        <div>
            <Label label="Selections">
                <ValidValueInputContainer>
                    <TextInput
                        placeholder="Enter a selection value"
                        width="46rem"
                        maxLength={validValueMaxLength}
                        value={validValue}
                        onChange={(event) => setValidValue(event.target.value)}
                        onKeyDown={onTextInputKeyDown}
                    />
                    <Button
                        type="button"
                        appearance="subtle"
                        spacing="compact"
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={onAddValidValue}
                        disabled={!inputIsValid}
                    >
                        Add
                    </Button>
                </ValidValueInputContainer>
            </Label>

            {validValues && validValues.length !== 0 && (
                <DndProvider backend={HTML5Backend}>
                    <ValuesListRenderer values={validValues} setValues={onChange} />
                </DndProvider>
            )}
        </div>
    );
}

const ValuesListRenderer = ({
    values,
    setValues,
}: {
    values: string[];
    setValues: (values: string[]) => void;
}) => {
    const { secondaryText, deleteButton } = useThemeValues();

    const moveItem = useCallback(
        (item: string, targetIdx: number) => {
            const newValues = values;
            const idx = newValues.indexOf(item);

            if (idx === targetIdx) {
                return;
            }

            newValues.splice(idx, 1);
            newValues.splice(targetIdx, 0, item);

            setValues(newValues);
        },
        [setValues, values]
    );

    const removeValue = (valueToRemove: string) => {
        setValues(values.filter((value) => value !== valueToRemove));
    };

    const findIndex = useCallback((v: string) => values.indexOf(v), [values]);

    return (
        <ValidValuesListContainer>
            {values.map((value) => (
                <ValueRenderer
                    key={value}
                    value={value}
                    color={secondaryText}
                    hoverColor={deleteButton}
                    findIndex={findIndex}
                    moveItem={moveItem}
                    onRemoveClick={() => removeValue(value)}
                />
            ))}
        </ValidValuesListContainer>
    );
};

const ValueRow = styled.div`
    padding: 0.25rem 1rem;
    background-color: ${backgroundColor};
    cursor: move;
`;

type ValueProps = Readonly<{
    value: string;
    color: string;
    hoverColor: string;
    onRemoveClick: (value: string) => void;
    moveItem: (value: string, targetIndex: number) => void;
    findIndex: (value: string) => number;
}>;

type DragItemProps = { value: string; originalIndex: number };

const ValueRenderer = ({
    value,
    color,
    hoverColor,
    onRemoveClick,
    moveItem,
    findIndex,
}: ValueProps) => {
    const curIdx = findIndex(value);
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: 'value',
            item: { value, originalIndex: curIdx },
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
            end: (item, monitor) => {
                const { value: draggedValue, originalIndex } = item;
                const didDrop = monitor.didDrop();
                if (!didDrop) {
                    moveItem(draggedValue, originalIndex);
                }
            },
        }),
        [value, moveItem, curIdx]
    );

    const [, drop] = useDrop<DragItemProps, unknown, unknown>(
        () => ({
            accept: 'value',
            hover({ value: draggedValue }) {
                if (draggedValue !== value) {
                    moveItem(draggedValue, findIndex(value));
                }
            },
        }),
        [findIndex, moveItem]
    );
    const opacity = isDragging ? 0 : 1;

    return (
        <ValueRow
            className="flex justify-between"
            key={value}
            style={{ opacity }}
            ref={(node) => drag(drop(node))}
        >
            <div className="flex-row align-center">
                <FontAwesomeIcon className="mr-20" icon={faGripLines} color={color} />
                <div className="long-text-wrap">{value}</div>
            </div>
            <HoverIconButton
                icon={faTrash}
                hoverIcon={faTrashSolid}
                color={color}
                hoverColor={hoverColor}
                size="14"
                onClick={() => onRemoveClick(value)}
            />
        </ValueRow>
    );
};
